import { useContext, useEffect, useRef, useState } from 'react';
import Geolocation from 'ol/Geolocation';
import Feature from 'ol/Feature';
import Point from 'ol/geom/Point';
import VectorSource from 'ol/source/Vector';
import VectorLayer from 'ol/layer/Vector';
import { Circle as CircleStyle, Fill, Stroke, Style } from 'ol/style';
import MapContext from 'contexts/MapContext';
import { Geometry } from 'ol/geom';
import { useIntl } from 'react-intl';
import useSessionStorage from 'utils/useSessionStorage';
import { Coordinate } from 'ol/coordinate';

const LocateControl: React.FC = () => {
  const intl = useIntl();
  const map = useContext(MapContext);
  const {
    value: isActive,
    setSessionValue: setIsActive,
    removeSessionValue: unsetActive,
  } = useSessionStorage<Coordinate>('IS_TRACKING_ACTIVE');

  const geoRef = useRef<Geolocation | null>(null);
  const layerRef = useRef<VectorLayer<VectorSource<Geometry>> | null>(null);

  const positionFeatureRef = useRef<Feature<Point>>(new Feature(new Point([0, 0])));
  const accuracyFeatureRef = useRef<Feature<Geometry>>(new Feature());

  useEffect(() => {
    if (!map) return;

    const view = map.getView();

    const source = new VectorSource<Geometry>();
    const layer = new VectorLayer({
      source: source,
      zIndex: 100,
    });

    layerRef.current = layer;
    map.addLayer(layer);

    const geolocation = new Geolocation({
      trackingOptions: { enableHighAccuracy: true },
      projection: view.getProjection(),
    });
    geoRef.current = geolocation;

    positionFeatureRef.current.setStyle(
      new Style({
        image: new CircleStyle({
          radius: 8,
          fill: new Fill({ color: '#518B33' }),
          stroke: new Stroke({ color: '#ffffff', width: 3 }),
        }),
      })
    );

    accuracyFeatureRef.current.setStyle(
      new Style({
        fill: new Fill({
          color: 'rgba(0, 0, 0, 0)',
        }),
        stroke: new Stroke({
          color: '#518B33',
          width: 2,
        }),
      })
    );

    source.addFeatures([accuracyFeatureRef.current, positionFeatureRef.current]);

    const positionListenerRef = geolocation.on('change:position', () => {
      const coordinates = geolocation.getPosition();
      positionFeatureRef.current.setGeometry(coordinates ? new Point(coordinates) : undefined);
    });

    const accuracyGeometryListenerRef = geolocation.on('change:accuracyGeometry', () => {
      const accuracy = geolocation.getAccuracyGeometry();
      accuracyFeatureRef.current.setGeometry(accuracy || undefined);
    });

    geolocation.on('error', (error: any) => {
      console.error('Geolocation error:', error.message);
      unsetActive();
    });

    if (isActive && geoRef.current.getTracking() === false) {
      geoRef.current.setTracking(true);
      view.animate({ center: isActive, zoom: 9, duration: 500 });
      positionFeatureRef.current.setGeometry(new Point(isActive));
    }

    return () => {
      map.removeLayer(layer);
      geolocation.setTracking(false);
      geolocation.un('change:position', positionListenerRef.listener);
      geolocation.un('change:accuracyGeometry', accuracyGeometryListenerRef.listener);
    };
  }, [map]);

  const handleLocateClick = (): void => {
    if (!map || !geoRef.current) return;
    const view = map.getView();

    if (isActive) {
      geoRef.current.setTracking(false);
      positionFeatureRef.current.setGeometry(undefined);
      accuracyFeatureRef.current.setGeometry(undefined);
      unsetActive();
    } else {
      geoRef.current.setTracking(true);

      const coords = geoRef.current.getPosition();
      if (coords) {
        setIsActive(coords);
        view.animate({ center: coords, zoom: 9, duration: 500 });
      } else {
        geoRef.current.once('change:position', () => {
          const freshCoords = geoRef.current?.getPosition();
          if (freshCoords && !isActive) {
            setIsActive(freshCoords);
            view.animate({ center: freshCoords, zoom: 9, duration: 500 });
          }
        });
      }
    }
  };

  return (
    <div id="locate-control" className="ol-control">
      <button
        className={isActive ? 'ol-locate active' : 'ol-locate'}
        type="button"
        onClick={handleLocateClick}
        title={intl.formatMessage({ id: 'map.locate' })}
      >
        <i className="fa-solid fa-location-crosshairs"></i>
      </button>
    </div>
  );
};

export default LocateControl;
